/**
 * Data table.
 *
 * `text-align: start` rather than `left` on every cell, so the whole table mirrors in Arabic
 * without a second stylesheet. Numeric columns opt into tabular figures and end alignment, which
 * is what makes a column of amounts readable.
 *
 * The scroll container is on the table, not the page: a wide table must scroll inside its own box
 * rather than making the whole screen scroll sideways.
 */

import type { ReactNode } from "react";

import { cx } from "./cx";

export interface Column<Row> {
  key: string;
  header: string;
  /** Right/end aligned with tabular figures. Use for anything a reader compares down a column. */
  numeric?: boolean;
  render: (row: Row) => ReactNode;
}

export interface TableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Shown in place of the body when there is nothing. */
  empty?: ReactNode;
  className?: string;
}

export function Table<Row>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  className,
}: TableProps<Row>): JSX.Element {
  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    // The scroll container, and the only element allowed to scroll sideways on these screens. A
    // table wider than its column has to go somewhere, and letting the *page* take the overflow
    // moves the side rail off screen.
    <div className="min-h-0 flex-1 overflow-auto">
      {/* `min-w` as well as `w-full`, and the pair is the point: `w-full` alone makes the table
          shrink to whatever space there is, so six columns on a phone are crushed to a few
          characters each and the wrapper's `overflow-auto` never has anything to scroll. With a
          floor the columns keep a readable width and the container scrolls instead — which is
          what the design does for the answer table too. */}
      <table
        className={cx("w-full min-w-[640px] border-collapse text-body-sm", className)}
      >
        {/* Visually hidden rather than absent: a table with no caption is unnavigable by screen
            reader, and a visible one competes with the card heading above it. */}
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              // `sticky`: these tables are the full height of the window and run to hundreds of
              // rows, and a header that scrolls away leaves a screen of unlabelled columns. The
              // background is opaque for the same reason — a translucent one lets rows show
              // through as they pass under it.
              <th
                key={column.key}
                scope="col"
                className={cx(
                  "sticky top-0 z-panel border-b border-borderSubtle bg-surface px-3 py-3",
                  "text-label-md font-medium text-textMuted",
                  column.numeric ? "text-end" : "text-start",
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="transition-colors hover:bg-primarySubtle">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cx(
                    "border-b border-borderSubtle px-3 py-3 align-middle text-textPrimary",
                    column.numeric
                      ? "text-end font-mono tabular-nums"
                      : "text-start",
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
